/**
 * ChatMessageRemapper
 *
 * Converts between ChatMessageDto and Firestore wire JSON, and between
 * ChatMessageEntity and ChatMessageDto.
 *
 * Flutter source: lib/data/models/chat_message.dart (+ chat_message.g.dart).
 *
 * JsonKey translation table (matches Flutter `@JsonKey` annotations on the
 * Dart model, lib/data/models/chat_message.dart:23-26):
 *   - Dart property `relativeServerPath` <-> JSON key `serverPath`
 *   - Dart property `relativeThumbnailPath` <-> JSON key `thumbnail`
 * Every other property name on the DTO is also its JSON key.
 *
 * Defaults applied by `fromJson` (matches the generated factory in
 * chat_message.g.dart:9-13):
 *   - `success` defaults to `true`
 *   - `seenByPeer` defaults to `false`
 *   - `giftInstances` defaults to `1`
 *
 * Why `giftInstances` defaults to `1` instead of `0` / `null`:
 *   Every read of `giftInstances` (web: `GiftBubble.tsx`; Flutter: `chat_screen.dart`,
 *   `video_call_screen.dart`, `live_streaming_screen.dart`) gates on
 *   `giftInstances > 1` before rendering a quantity prefix. So the parsed value
 *   when the field is absent is semantically "don't render a quantity" — `0`,
 *   `null`, and `1` would all behave identically at every current read site.
 *   We keep the default at `1` for two reasons:
 *   (a) Flutter parity (`@JsonKey(defaultValue: 1)` on `chat_message.dart:24`);
 *       any change here must land on Flutter too.
 *   (b) Historical Firestore docs that pre-date the `giftInstances` field have
 *       no value for it, and a default of `1` honestly represents the historical
 *       baseline ("a single instance was sent") rather than an undercounted `0`.
 *   The cloud function (`send_gift.ts:108, 142, 183`) always writes the field
 *   explicitly for newly-sent gifts, so this default only ever materializes for
 *   legacy documents.
 *
 * Nested objects (`latestLiveGiftInfo`, `shareInfo`) are parsed by their
 * canonical remappers — `LatestLiveGiftInfoRemapper` / `ShareInfoRemapper` —
 * so this file does NOT duplicate their default-application logic.
 */

import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatMessageDto } from '@/data/dtos/chat/chatMessageDto';
import type { ShareInfoDto } from '@/data/dtos/share/shareDto';
import type { JsonObject } from '@/data/types/json';
import { LatestLiveGiftInfoRemapper } from '@/domain/remappers/info/latestLiveGiftInfoRemapper';
import { ShareInfoRemapper } from '@/domain/remappers/share/shareInfoRemapper';

export class ChatMessageRemapper {
  /**
   * Parse a Firestore-shaped wire JSON object into a ChatMessageDto.
   *
   * Mirrors Flutter's generated `_$ChatMessageFromJson`
   * (chat_message.g.dart:9-44) including the two `@JsonKey` overrides
   * (`serverPath` -> `relativeServerPath`, `thumbnail` -> `relativeThumbnailPath`)
   * and the three defaults (`success=true`, `seenByPeer=false`, `giftInstances=1`).
   * See the file-level docblock for why `giftInstances` defaults to `1`.
   */
  static fromJson(data: JsonObject): ChatMessageDto {
    const latestLiveGiftInfoRaw =
      data['latestLiveGiftInfo'] as JsonObject | undefined | null;
    const shareInfoRaw = data['shareInfo'] as JsonObject | undefined | null;

    const dto: ChatMessageDto = {
      documentId: (data['documentId'] as string | undefined) ?? '',
      chatId: (data['chatId'] as string | undefined) ?? '',
      idFrom: (data['idFrom'] as string | undefined) ?? '',
      idTo: (data['idTo'] as string | undefined) ?? '',
      timestamp: (data['timestamp'] as number | undefined) ?? 0,
      type: (data['type'] as number | undefined) ?? 0,
      // Flutter defaults (chat_message.g.dart:10-12). `giftInstances` keeps the
      // `1` default by design — see the file-level docblock for the rationale.
      success: (data['success'] as boolean | undefined) ?? true,
      seenByPeer: (data['seenByPeer'] as boolean | undefined) ?? false,
      giftInstances: (data['giftInstances'] as number | undefined) ?? 1,
      users: Array.isArray(data['users'])
        ? (data['users'] as unknown[]).map((e) => e as string)
        : [],
    };

    // Optional fields: assign only when the source key is present (non-undefined).
    // This keeps the DTO shape lossless for round-tripping `toJson -> fromJson`.
    if (data['content'] !== undefined) {
      dto.content = data['content'] as string | undefined;
    }
    if (data['localPath'] !== undefined) {
      dto.localPath = data['localPath'] as string | undefined;
    }
    if (data['isPortrait'] !== undefined) {
      dto.isPortrait = data['isPortrait'] as boolean | undefined;
    }
    if (data['isUploading'] !== undefined) {
      dto.isUploading = data['isUploading'] as boolean | undefined;
    }
    if (data['aspectRatio'] !== undefined) {
      dto.aspectRatio = data['aspectRatio'] as number | undefined;
    }
    // JsonKey overrides: Firestore stores `serverPath` / `thumbnail`,
    // the DTO field names are `relativeServerPath` / `relativeThumbnailPath`.
    if (data['serverPath'] !== undefined) {
      dto.relativeServerPath = data['serverPath'] as string | undefined;
    }
    if (data['thumbnail'] !== undefined) {
      dto.relativeThumbnailPath = data['thumbnail'] as string | undefined;
    }
    if (data['giftThumbnail'] !== undefined) {
      dto.giftThumbnail = data['giftThumbnail'] as string | undefined;
    }
    if (data['giftName'] !== undefined) {
      dto.giftName = data['giftName'] as string | undefined;
    }
    if (data['charge'] !== undefined) {
      dto.charge = data['charge'] as number | undefined;
    }
    if (data['videoCallId'] !== undefined) {
      dto.videoCallId = data['videoCallId'] as string | undefined;
    }
    if (data['callerStreamingId'] !== undefined) {
      dto.callerStreamingId = data['callerStreamingId'] as number | undefined;
    }
    if (data['audienceStreamingId'] !== undefined) {
      dto.audienceStreamingId = data['audienceStreamingId'] as number | undefined;
    }
    if (data['isLive'] !== undefined) {
      dto.isLive = data['isLive'] as boolean | undefined;
    }
    if (data['callDuration'] !== undefined) {
      dto.callDuration = data['callDuration'] as number | undefined;
    }
    // Nested objects: parse only when the source value is non-null
    // (matches Flutter's `json['latestLiveGiftInfo'] == null ? null : …`).
    // Delegate to the canonical remappers so default-application (e.g.
    // `MediaInfoRemapper.fromJson`'s `aspectRatio` portrait default) is
    // applied uniformly across every Firestore-read entry point.
    if (latestLiveGiftInfoRaw) {
      dto.latestLiveGiftInfo = LatestLiveGiftInfoRemapper.fromJson(latestLiveGiftInfoRaw);
    }
    if (shareInfoRaw) {
      dto.shareInfo = ShareInfoRemapper.fromJson(shareInfoRaw);
    }

    return dto;
  }

  /**
   * Serialize a ChatMessageDto to Firestore wire JSON.
   *
   * Honors the two `@JsonKey` overrides from Flutter's generated `toJson`
   * (chat_message.g.dart:46-84): emits `serverPath` / `thumbnail` keys instead
   * of the DTO property names `relativeServerPath` / `relativeThumbnailPath`.
   * Optional fields whose source value is `undefined` are omitted entirely
   * (Firestore rejects `undefined`; the rest of `toDto` already follows this rule).
   */
  static toJson(dto: ChatMessageDto): JsonObject {
    const json: JsonObject = {
      documentId: dto.documentId,
      chatId: dto.chatId,
      idFrom: dto.idFrom,
      idTo: dto.idTo,
      timestamp: dto.timestamp,
      type: dto.type,
      success: dto.success,
      seenByPeer: dto.seenByPeer,
      giftInstances: dto.giftInstances,
      users: dto.users,
    };

    if (dto.content !== undefined) json.content = dto.content;
    if (dto.localPath !== undefined) json.localPath = dto.localPath;
    if (dto.isPortrait !== undefined) json.isPortrait = dto.isPortrait;
    if (dto.isUploading !== undefined) json.isUploading = dto.isUploading;
    if (dto.aspectRatio !== undefined) json.aspectRatio = dto.aspectRatio;
    // JsonKey overrides — emit the Flutter wire keys, NOT the DTO property names.
    if (dto.relativeServerPath !== undefined) json.serverPath = dto.relativeServerPath;
    if (dto.relativeThumbnailPath !== undefined) json.thumbnail = dto.relativeThumbnailPath;
    if (dto.giftThumbnail !== undefined) json.giftThumbnail = dto.giftThumbnail;
    if (dto.giftName !== undefined) json.giftName = dto.giftName;
    if (dto.charge !== undefined) json.charge = dto.charge;
    if (dto.videoCallId !== undefined) json.videoCallId = dto.videoCallId;
    if (dto.callerStreamingId !== undefined) json.callerStreamingId = dto.callerStreamingId;
    if (dto.audienceStreamingId !== undefined) json.audienceStreamingId = dto.audienceStreamingId;
    if (dto.isLive !== undefined) json.isLive = dto.isLive;
    if (dto.callDuration !== undefined) json.callDuration = dto.callDuration;
    if (dto.latestLiveGiftInfo !== undefined) json.latestLiveGiftInfo = dto.latestLiveGiftInfo;
    if (dto.shareInfo !== undefined) json.shareInfo = dto.shareInfo;

    return json;
  }

  /**
   * Build a `ChatMessageEntity` from a `ChatMessageDto`.
   *
   * The DTO and entity are field-identical on every required and most
   * optional fields, so this is mostly a per-field copy guarded by
   * `!== undefined` to keep the entity shape lossless. The one path that
   * does real work is `shareInfo`: the nested `MediaInfoDto` / `UsersInfoDto`
   * widen optional fields that the entity tightens (e.g. `MediaInfoDto`
   * leaves `aspectRatio` / `allowUnlock` optional, the entity requires
   * them with defaults), so it must route through the canonical
   * `UsersInfoRemapper.fromDto` / `MediaInfoRemapper.fromDto`.
   *
   * `latestLiveGiftInfo` is structurally identical between layers, so a
   * direct assignment is safe.
   */
  static fromDto(dto: ChatMessageDto): ChatMessageEntity {
    const entity: ChatMessageEntity = {
      documentId: dto.documentId,
      chatId: dto.chatId,
      idFrom: dto.idFrom,
      idTo: dto.idTo,
      timestamp: dto.timestamp,
      type: dto.type,
      success: dto.success,
      seenByPeer: dto.seenByPeer,
      giftInstances: dto.giftInstances,
      users: dto.users,
    };

    if (dto.content !== undefined) entity.content = dto.content;
    if (dto.localPath !== undefined) entity.localPath = dto.localPath;
    if (dto.isPortrait !== undefined) entity.isPortrait = dto.isPortrait;
    if (dto.isUploading !== undefined) entity.isUploading = dto.isUploading;
    if (dto.aspectRatio !== undefined) entity.aspectRatio = dto.aspectRatio;
    if (dto.relativeServerPath !== undefined) entity.relativeServerPath = dto.relativeServerPath;
    if (dto.relativeThumbnailPath !== undefined) entity.relativeThumbnailPath = dto.relativeThumbnailPath;
    if (dto.giftThumbnail !== undefined) entity.giftThumbnail = dto.giftThumbnail;
    if (dto.giftName !== undefined) entity.giftName = dto.giftName;
    if (dto.charge !== undefined) entity.charge = dto.charge;
    if (dto.videoCallId !== undefined) entity.videoCallId = dto.videoCallId;
    if (dto.callerStreamingId !== undefined) entity.callerStreamingId = dto.callerStreamingId;
    if (dto.audienceStreamingId !== undefined) entity.audienceStreamingId = dto.audienceStreamingId;
    if (dto.isLive !== undefined) entity.isLive = dto.isLive;
    if (dto.callDuration !== undefined) entity.callDuration = dto.callDuration;
    // Structurally identical between layers — direct assignment is safe.
    if (dto.latestLiveGiftInfo !== undefined) entity.latestLiveGiftInfo = dto.latestLiveGiftInfo;
    if (dto.shareInfo !== undefined) entity.shareInfo = ShareInfoRemapper.fromDto(dto.shareInfo);

    return entity;
  }

  static toDto(entity: ChatMessageEntity): ChatMessageDto {
    // Required fields first; optional fields assigned only when defined
    // (Firestore rejects `undefined` values).
    const dto: ChatMessageDto = {
      documentId: entity.documentId,
      chatId: entity.chatId,
      idFrom: entity.idFrom,
      idTo: entity.idTo,
      timestamp: entity.timestamp,
      type: entity.type,
      success: entity.success,
      seenByPeer: entity.seenByPeer,
      giftInstances: entity.giftInstances,
      users: entity.users,
    };

    if (entity.content !== undefined) dto.content = entity.content;
    if (entity.localPath !== undefined) dto.localPath = entity.localPath;
    if (entity.isPortrait !== undefined) dto.isPortrait = entity.isPortrait;
    if (entity.isUploading !== undefined) dto.isUploading = entity.isUploading;
    if (entity.aspectRatio !== undefined) dto.aspectRatio = entity.aspectRatio;
    // Note: relativeServerPath/relativeThumbnailPath map to 'serverPath'/'thumbnail' in Firestore
    if (entity.relativeServerPath !== undefined) dto.relativeServerPath = entity.relativeServerPath;
    if (entity.relativeThumbnailPath !== undefined) dto.relativeThumbnailPath = entity.relativeThumbnailPath;
    if (entity.giftThumbnail !== undefined) dto.giftThumbnail = entity.giftThumbnail;
    if (entity.giftName !== undefined) dto.giftName = entity.giftName;
    if (entity.charge !== undefined) dto.charge = entity.charge;
    if (entity.videoCallId !== undefined) dto.videoCallId = entity.videoCallId;
    if (entity.callerStreamingId !== undefined) dto.callerStreamingId = entity.callerStreamingId;
    if (entity.audienceStreamingId !== undefined) dto.audienceStreamingId = entity.audienceStreamingId;
    if (entity.isLive !== undefined) dto.isLive = entity.isLive;
    if (entity.callDuration !== undefined) dto.callDuration = entity.callDuration;
    if (entity.latestLiveGiftInfo !== undefined) dto.latestLiveGiftInfo = entity.latestLiveGiftInfo;

    if (entity.shareInfo) {
      const shareInfo: ShareInfoDto = {
        type: entity.shareInfo.type,
        shareLink: entity.shareInfo.shareLink,
        usersInfo: entity.shareInfo.usersInfo,
      };
      if (entity.shareInfo.mediaInfo) {
        shareInfo.mediaInfo = entity.shareInfo.mediaInfo;
      }
      dto.shareInfo = shareInfo;
    }

    return dto;
  }
}
